/**
 * Fact carry-forward — the anti-regression layer for AI-driven document
 * rebuilds.
 *
 * THE BUG CLASS THIS PREVENTS (found in browser testing, 29 Jul 2026): a fact
 * the user has already supplied ("meeting ran 4–5:30 P.M.", "FY ended 31st
 * March 2026") regressed to a [PLACEHOLDER] after Vee applied an unrelated
 * scoped edit. Two independent leaks caused it:
 *
 *   1. HTML-only facts. The dock's "Fill in the remaining details" panel
 *      spliced values into the draft HTML but never into the form state — so
 *      the page context, the drafting tool's args, and the studio's Generate
 *      payload all still said the fact was unknown, and the next regeneration
 *      re-emitted the placeholder.
 *   2. Echo clobber. The drafting tools echo a FULL config in which every
 *      argument the model did not restate is '' — and both studios applied
 *      that echo over live state, erasing facts the model simply didn't
 *      repeat. ('' from a revision echo means "not restated", never "clear".)
 *
 * The invariant this module enforces (the same one Word's Copilot and Google
 * Docs' assistant follow): THE FORM/CONFIG IS THE FACT STORE. Documents are
 * projections of it. Anything that teaches the document a fact must write it
 * through to the store, and nothing that regenerates the projection may treat
 * "absent from this response" as "unknown".
 */
import { parseDisplayTime } from '../context/MeetingConfigContext';
import { fillPlaceholderInHtml } from './draftUtils';

/* ── Value conversions ────────────────────────────────────────────────────── */

// "4:00 P.M." / "16:00" → "16:00" ('' when unparseable — an unparsed value is
// omitted from the patch rather than stored where a TimePicker expects HH:MM).
const toTime24 = (v) => parseDisplayTime(String(v || '').trim());

// "16:00" → "4:00 P.M." (document display form); '' when not HH:MM.
export const toDisplayTime = (v) => {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(String(v || '').trim());
  if (!m) return '';
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'P.M.' : 'A.M.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
};

// "Monday, 05 October 2026" / "5 August 2026" / "2026-10-05" → "2026-10-05".
// '' when unparseable. Local-time getters, never toISOString (TZ-shift safe).
const toISODate = (v) => {
  const s = String(v || '').replace(/^[A-Za-z]+day,\s*/i, '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// "2026-10-05" → "5 October 2026" (document display form).
const toDisplayDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || '').trim());
  if (!m) return String(v || '').trim();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const trimmed = (v) => String(v || '').trim();

/* ── Minutes: placeholder token ↔ form field ──────────────────────────────── */

// Normalized "[Start Time]" → "START TIME".
const normalizeToken = (ph) => String(ph || '')
  .replace(/^\[|\]$/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

// Token → update_page_config field name (the drafting-tool vocabulary the
// bridge's SCALAR_KEY_MAP already translates to MeetingConfigContext keys).
// `toConfig` shapes the document string for the form control; `fromConfig`
// shapes the form value for the document. Person tokens ([DIRECTOR 1 NAME],
// [DIN 1]) are deliberately absent — attendance is checkbox state, not a
// scalar, and a wrong write-through there would corrupt the register.
const MINUTES_TOKEN_FIELDS = {
  'START TIME': { field: 'startTime', toConfig: toTime24, fromConfig: toDisplayTime },
  'END TIME': { field: 'endTime', toConfig: toTime24, fromConfig: toDisplayTime },
  VENUE: { field: 'venue' },
  'MODE OF MEETING': { field: 'mode' },
  CHAIRPERSON: { field: 'chairperson' },
  CHAIRMAN: { field: 'chairperson' },
  'COMPANY SECRETARY': { field: 'companySecretary' },
  'STATUTORY AUDITOR': { field: 'statutoryAuditor' },
  'FINANCIAL YEAR': { field: 'financialYear' },
  FY: { field: 'financialYear' },
  'NOTICE DATE': { field: 'noticeDate', toConfig: toISODate, fromConfig: toDisplayDate },
  DATE: { field: 'date', toConfig: toISODate, fromConfig: toDisplayDate },
  'MEETING DATE': { field: 'date', toConfig: toISODate, fromConfig: toDisplayDate },
  'PROPOSED AGM DATE': { field: 'proposedAgmDate', toConfig: toISODate, fromConfig: toDisplayDate },
  'PROPOSED AGM VENUE': { field: 'proposedAgmVenue' },
  'AGM VENUE': { field: 'proposedAgmVenue' },
};

// The bridge stores these under different MeetingConfigContext keys — needed
// when reading the LIVE config back out for a document fill.
const FIELD_TO_CONFIG_KEY = { financialYear: 'fy' };

/**
 * Filled placeholder values → an update_page_config-shaped patch, so the dock
 * can write the facts through the SAME code path a real tool patch takes
 * (bridge validation, field flash, toast). Unknown tokens and values that fail
 * conversion are dropped — a skipped write-through is recoverable (the user
 * still sees the filled document), a corrupted form value is not.
 *
 * @param {Object<string,string>} values  placeholder token → document string
 * @returns {Object<string,string>}       tool-vocabulary field → form value
 */
export function minutesPatchFromPlaceholders(values = {}) {
  const patch = {};
  for (const [token, raw] of Object.entries(values)) {
    const spec = MINUTES_TOKEN_FIELDS[normalizeToken(token)];
    if (!spec) continue;
    const value = (spec.toConfig || trimmed)(raw);
    if (value) patch[spec.field] = value;
  }
  return patch;
}

/**
 * Deterministically fill a regenerated minutes draft's placeholders from facts
 * the form already holds — the belt-and-braces guarantee that a scoped edit
 * can never RE-OPEN a question the user has answered, even when the model
 * forgot to restate the fact in its tool call.
 *
 * @param {{html?: string, plainText?: string}} draft
 * @param {object} config  MeetingConfigContext-shaped config (live ∪ echo)
 * @returns {{html: string, plainText: string, filled: number}}
 */
export function fillMinutesDraftFromConfig(draft = {}, config = {}) {
  let html = String(draft.html || '');
  let plainText = String(draft.plainText || '');
  let filled = 0;
  const esc = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  for (const [token, spec] of Object.entries(MINUTES_TOKEN_FIELDS)) {
    const key = FIELD_TO_CONFIG_KEY[spec.field] || spec.field;
    const value = (spec.fromConfig || trimmed)(config[key]);
    if (!value) continue;
    const literal = `[${token}]`;
    if (!html.includes(literal)) continue;
    html = fillPlaceholderInHtml(html, literal, esc(value));
    plainText = plainText.split(literal).join(value);
    filled += 1;
  }
  return { html, plainText, filled };
}

/* ── Tool-echo hygiene ─────────────────────────────────────────────────────── */

const isEmptyEchoValue = (v) => v === '' || v === null || v === undefined
  || (Array.isArray(v) && v.length === 0);

const dropEmpty = (obj = {}) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => !isEmptyEchoValue(v)),
);

/**
 * Prune a draft_meeting_minutes meetingConfig echo before it reaches
 * restoreConfig. The tool builds its echo from str(args.X), so every fact the
 * model did not RESTATE arrives as '' (scalars) or [] (lists) — semantically
 * "no information", never "clear this field".
 */
export function pruneMeetingConfigEcho(mc) {
  if (!mc || typeof mc !== 'object') return mc;
  const out = { ...mc };
  if (out.config) out.config = dropEmpty(out.config);
  for (const k of ['customItems', 'absentDirectors', 'invitees']) {
    if (isEmptyEchoValue(out[k])) delete out[k];
  }
  // directorsChecked/agendaChecked {} are equally "nothing restated".
  for (const k of ['directorsChecked', 'agendaChecked']) {
    if (out[k] && typeof out[k] === 'object' && !Object.keys(out[k]).length) delete out[k];
  }
  return out;
}

/**
 * Merge a draft_auditor_report reportConfig echo over the live studio config.
 * Non-empty incoming values win (the model's latest statement of a fact);
 * empty strings lose to whatever the studio already knows. Booleans pass
 * through — false is a computed decision in that tool, not an absence.
 */
export function mergeReportPrefillConfig(current = {}, incoming = {}) {
  return { ...current, ...dropEmpty(incoming) };
}
